<template>
    <div v-if="platformAvailable" class="platform-records">
        <h2>Select a {{ platformRecordType }}:</h2>
        <p class="mb-4">
            {{ displaySubHeader }} <br>
            If you need to import data from a different account,
            <span
                v-if="sendingFiles || loading"
                class="ml-1 mr-1 font-weight-bold text-nowrap"
            >click here</span>
            <third-party-platform-login
                v-else
                :button-display="false"
                :external-id="value_externalId"
                :order-app-resource="orderAppResource"
                :platform="value_platform"
                :redirect-page="redirectPage"
                display-text="click here"
            />
            to re-authenticate.
        </p>
        <b-form
            @submit.prevent="submitPrevent"
        >
            <template v-if="!loading">
                <b-form-group>
                    <b-form-radio
                        v-model="value_selected"
                        :disabled="sendingFiles || loading"
                        class="mb-3"
                        name="selectRecord"
                        value="createRecord"
                    >
                        <b>Create a new {{ platformLabel }} {{ platformRecordType }} for this order.</b>
                    </b-form-radio>
                    <template v-if="!loading && platformRecords.length">
                        <template v-for="(record, index) in platformRecords">
                            <b-form-radio
                                :key="'record' + index"
                                v-model="value_selected"
                                :disabled="sendingFiles"
                                :value="record"
                                class="mb-2"
                                name="selectRecord"
                            >
                                <b>{{ record.propertyAddress || record.name }}</b><br>
                                <b class="blockquote-footer">Seller: {{
                                        getSellerName(record) || "No information available"
                                    }}</b>
                            </b-form-radio>
                            <br>
                        </template>
                    </template>
                    <b-alert v-else class="mt-4" show>
                        No {{ platformRecordType }}s received.
                        If you have {{ platformRecordType }}s saved on your {{ platformLabel }} account, please try to
                        re-authenticate.
                    </b-alert>
                    <template v-if="allErrors.length">
                        <b-form-invalid-feedback
                            v-for="(error, errIndex) in allErrors"
                            :key="errIndex"
                            class="d-block"
                        >
                            <i class="fal fa-exclamation-circle"/> {{ error }}
                        </b-form-invalid-feedback>
                    </template>
                </b-form-group>
                <br>
                <div
                    class="d-flex justify-content-center finish-button-container"
                >
                    <b-btn
                        v-if="submitGetRecordVisible"
                        :disabled="sendingFiles || loading"
                        class="small-button"
                        size="lg"
                        type="submit"
                        variant="primary"
                    >
                        {{ submitGetRecordLabel }}
                    </b-btn>
                    <b-btn
                        v-else-if="submitSendFilesVisible"
                        :disabled="sendingFiles || loading"
                        size="lg"
                        type="button"
                        variant="primary"
                        @click="sendFilesToPlatform"
                    >
                        <b-spinner
                            v-if="sendingFiles"
                            class="mr-1"
                            small
                        />
                        Send files
                    </b-btn>
                </div>
            </template>
        </b-form>
        <div id="loader-single-spa" v-if="loading" style="position: relative; margin-top: 4rem;"></div>
    </div>
</template>

<script>
import {thirdPartyService} from "@packages/integrations/service";
import ThirdPartyPlatformLogin from "./ThirdPartyPlatformLogin";
import ShowNotificationMixin from '@sellers-shield-home-protection/ss-ui-kit/src/mixins/ShowNotificationMixin';

export default {
    name: "ThirdPartyPlatformRecords",
    mixins: [
        ShowNotificationMixin,
    ],
    components: {
        ThirdPartyPlatformLogin
    },
    props: {
        externalId: {
            type: String,
            default: 'NEW-' + Math.round(Math.random() * 100000000000000000),
        },
        orderAppResource: {
            type: Object,
            default: null,
        },
        platform: {
            type: String,
            default: null,
        },
        redirectPage: {
            type: String,
            required: true,
        },
        selectedRecord: {
            type: [Object, String],
            default: null,
        },
        contextId: {
            type: [String, Number, Boolean],
            default: null,
        },
        displayHeading: {
            type: String,
            default: '',
        },
        submitGetRecordVisible: {
            type: Boolean,
            default: true,
        },
        submitGetRecordLabel: {
            type: String,
            default: 'Continue',
        },
        submitSendFilesVisible: {
            type: Boolean,
            default: false,
        },
        errors: {
            type: Array,
            default: () => [],
        },
        states: {
            type: [Object, Array],
            default: () => [],
        }
    },
    data() {
        return {
            loading: false,
            sendingFiles: false,
            platformRecords: [],
            localErrors: [],
            value_contextId: this.contextId || null,
            value_platform: this.platform || '',
            value_externalId: this.externalId || null,
            value_selected: null,
            value_status: null,
        }
    },
    computed: {
        allErrors() {
            return [...new Set([
                ...this.errors,
                ...this.localErrors
            ])];
        },
        async platformAvailable() {
            return await thirdPartyService.isPlatformAvailable(this.value_platform || '');
        },
        platformLabel() {
            switch (this.value_platform) {
                case 'lonewolf':
                case 'zipform':
                    return 'zipForm® Plus';
                case 'dotloop':
                    return 'dotLoop®';
            }
            return 'external';
        },
        platformRecordType() {
            if (this?.displayHeading) {
                return this.displayHeading;
            }
            switch (this.value_platform) {
                case 'lonewolf':
                case 'zipform':
                    return 'transaction';
                case 'dotloop':
                    return 'loop';
            }
            return 'record';
        },
        displaySubHeader() {
            return `Create a new ${this?.platformLabel} ${this?.platformRecordType} or select an existing one from the list below.`;
        },
        displayNoRecords() {
            return `No ${this?.platformLabel} ${this?.platformRecordType}s were found in accessed account !`;
        },
        disclosureUuid() {
            return this.orderAppResource?.disclosure?.uuid || null;
        },
    },
    methods: {
        getAddressObj(record, fallbackRecordName = '') {
            return thirdPartyService.getPlatformRecordAddress(record, fallbackRecordName, this.value_platform);
        },
        getSeller(record) {
            return thirdPartyService.getPlatformRecordSeller(record);
        },
        getSellerName(record) {
            return this.getSeller(record)?.fullName;
        },
        submitPrevent() {
            this.$emit('third-party-platform-submit', this.value_selected);
        },
        async createNew(address = null) {
            this.$emit('third-party-platform-set-errors', this.localErrors = []);
            let newRecord = null;
            this.loading = true;

            try {
                if (address || !this.orderAppResource?.uuid) {
                    newRecord = await thirdPartyService.createRecord(this.value_contextId, address || 'new record', this.value_platform, this.orderAppResource?.uuid, 'order_detail');
                } else {
                    newRecord = await thirdPartyService.createRecordFromOrderResource(this.value_contextId, this.orderAppResource, this.states, this.value_platform, 'order_detail');
                }
                if (Boolean(newRecord?.id)) {
                    this.platformRecords = [
                        newRecord,
                        ...this.platformRecords
                    ];
                    this.value_selected = newRecord;

                    this.showNotification({
                        title: 'Success',
                        message: `New ${this?.platformLabel} ${this?.platformRecordType} created successfully!`,
                        type: 'success',
                    });
                }
            } catch (error) {
                this.localErrors.push(`Failed to create a new ${this.platformLabel} ${this.platformRecordType}`);
                this.showNotification({
                    title: 'Error',
                    message: this.localErrors[0],
                    type: 'error',
                });
                console.warn(error);
                newRecord = null;
            } finally {
                this.$nextTick(() => this.loading = false);
            }
            return newRecord;
        },
        async sendFilesToPlatform() {
            let result,
                record = this.value_selected === 'createRecord'
                    ? this.createNew()
                    : this.value_selected;

            if (this.value_selected !== 'createRecord') {
                this.$emit('third-party-platform-set-errors', this.localErrors = []);
            }

            if (!record?.id) {
                !record && this.localErrors.push(`Please select a ${this.platformLabel} ${this.platformRecordType}!`);
                return false;
            }

            try {
                this.sendingFiles = true;
                result = await thirdPartyService.sendFiles(this.value_contextId, record?.id, this.disclosureUuid, this.orderAppResource?.uuid, this.value_platform);

                if (result === 'No Files Sent') {
                    throw new Error(result);
                }

                !result || this.showNotification({
                    title: 'Success',
                    message: `${result.toLowerCase()} to ${this.platformLabel} successfully!`,
                    type: 'success',
                });
                this.$emit('third-party-platform-files-submit', result);
            } catch (error) {
                this.localErrors.push(`Error sending files to ${this.platformLabel}, please try again!`);
                this.showNotification({
                    title: 'Error',
                    message: this.localErrors[0],
                    type: 'error',
                });
                console.warn(error);
                result = null;
            } finally {
                this.sendingFiles = false;
            }
            return Boolean(result);
        },
    },

    async created() {
        this.platformRecords = [];

        if (!this.value_contextId) {
            let urlParams = new URLSearchParams(window.location.search),
                rquery = this.$route.query,
                zipformContextId = urlParams.get('zipformContextId') || rquery.zipformContextId,
                dotloopContextId = urlParams.get('dotloopContextId') || rquery.dotloopContextId
            ;
            if (zipformContextId) {
                this.value_contextId = zipformContextId;
                this.value_platform = 'zipform';
                this.value_status = 'active';
            } else if (dotloopContextId) {
                this.value_contextId = dotloopContextId;
                this.value_platform = 'dotloop';
                this.value_status = 'LISTING_FOR_SALE|ACTIVE_LISTING';
            }
        }
        if (this.value_contextId && this.value_platform) {
            this.loading = true;
            this.$emit('third-party-platform-context-id', this.value_contextId, this.value_platform);
            this.platformRecords = await thirdPartyService.getRecords(this.value_contextId, this.value_status, this.value_platform) || [];
            if (this.platformRecords.length > 0) {
                if (this.selectedRecord?.id) {
                    this.value_selected = this.platformRecords.find(record => record?.id == this.selectedRecord?.id) || null;
                }
            } else {
                this.value_selected = 'createRecord';
            }
            this.$nextTick(() => this.loading = false);
        } else {
            this.$emit('third-party-platform-context-id', null, '');
        }
    }
}
</script>